/*
 * Industry content — `en` (international market, source locale).
 *
 * This is the international variant of every industry (the old per-field `*Intl`
 * copy, resolved): generic time-registration framing, no belgium-specific pains/
 * features, and `compliance.slug` / `relatedArticles` pointing only at articles
 * that exist under `/en/` (the 4 market-agnostic posts). `nl.ts` carries the
 * belgium variant. Resolved by `industriesFor("en")` (also the SOURCE_LOCALE
 * fallback). Pure data (NFR10).
 */
import type { Industry } from "./types";

export const INDUSTRIES_BG: Industry[] = [
  {
    slug: "construction",
    name: "Строителство",
    headline: "Отчитане на времето за строителни екипи — отметка с потвърждение на местоположението, проектни часове и контрол на бюджета в едно приложение",
    intro: "Екипи, разпръснати по различни обекти, часове, нахвърляни на хартия, подизпълнителски часове, които е невъзможно да се съгласуват. Done-it дава на всеки обект непробиваем дигитален график за работно време, който захранва ведомостта Ви с едно кликване.",
    pains: [
      {
        title: "Няма надежден запис кой къде е работил",
        body: "При няколко активни обекта и сменящи се екипи ръчните графици са грешни по дефиниция. Работниците закръгляват, забравят записи или ги попълват дни по-късно — оставяйки Ви с данни за заплати, които не можете да защитите.",
      },
      {
        title: "Преразходът на бюджет излиза наяве твърде късно",
        body: "Докато забележите, че даден обект е изчерпал планираните си часове, маржът вече е изгубен. Няма обратно отброяване на живо — само неприятна изненада в момента на фактуриране.",
      },
      {
        title: "Подготовката на заплатите изяжда часове всеки месец",
        body: "Събирането на графиците, съгласуването им с проектите и подготовката на експорта за заплати отнема дни. Всеки запис е потенциална грешка.",
      },
    ],
    features: [
      {
        title: "Отметка с потвърждение на местоположението за всеки обект",
        body: "Едно докосване отчита работника, с потвърждение на местоположението, и свързва часовете с правилния проект. Без четци за карти, без терминали — само смартфонът, който екипът Ви вече носи.",
      },
      {
        title: "Оставащи часове на живо за всеки проект",
        body: "Задайте планираните часове като бюджет, когато откривате проекта. Работниците виждат обратно отброяване на това, което остава; Вие го виждате на живо в таблото си. Преразходът на бюджет е видим, преди да се случи.",
      },
      {
        title: "Управление на подизпълнители и присъствие",
        body: "Добавете подизпълнители към проект, задайте им отметка и проследявайте часовете им заедно с тези на собствения си екип — в един и същ преглед.",
      },
      {
        title: "Експорт за заплати с едно кликване",
        body: "Всички регистрирани часове — за всеки работник, за всеки проект — се експортират в чиста таблица за доставчика Ви на услуги за заплати. Без ръчна подготовка, без грешки.",
      },
    ],
    compliance: {
      label: "Дигитално отчитане на работното време",
      title: "Отметка с потвърждение на местоположението, създадена за строителни обекти",
      body: "Done-it осигурява отметка с потвърждение на местоположението, отчитане на времето по проекти и чист експорт за заплати за строителни екипи. За екипи, работещи на белгийски обекти, той също така автоматично захранва отметката на работното място.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Екипът пристига на обекта", body: "Всеки работник докосва веднъж, за да се отчете. GPS потвърждава местоположението, поставя времеви печат на записа и свързва часовете с проекта. Без хартия, без терминал." },
      { step: "Виждате всичко на живо", body: "Часове, оставащ бюджет, присъствие на подизпълнители и всяка допълнителна работа, докладвана от обекта — всичко в таблото Ви, в реално време." },
      { step: "Краят на месеца за минути", body: "Експортирайте всички часове за всеки работник и за всеки проект. Изпратете ги на доставчика си за заплати. Done-it автоматично генерира чисти, готови за одит отчети." },
    ],
    stats: [
      { value: "1 докосване", label: "за отметка на всеки обект — без терминал, без хартия" },
      { value: "1 кликване", label: "експорт за заплати, готов за доставчика Ви" },
      { value: "На живо", label: "преглед на оставащите часове за всеки проект и обект" },
    ],
    quote: {
      text: "Екипът ни сам се отчита на обекта. В края на месеца заплатите просто се събират — спряхме изцяло да гоним графиците.",
      who: "Собственик, строителна фирма · 14 служители",
      result: "5 спестени часа седмично за администрация",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "cleaning",
    name: "Почистване",
    headline: "Отчитане на времето за фирми за почистване — отметка с потвърждение на местоположението, доказателство за присъствие и заплати в едно приложение",
    intro: "Чистачите се местят между обекти през целия ден. Done-it проследява кой къде е в реално време — отметка с потвърждение на местоположението на всеки обект, автоматично приспадане на почивките и чист експорт за заплати в края на месеца.",
    pains: [
      {
        title: "Доказване на присъствие на всяко местоположение",
        body: "Клиентите и инспекторите очакват непробиваеми записи за присъствие. Ръчните листове могат да бъдат оспорени; отметките с потвърждение на местоположението — не.",
      },
      {
        title: "Екипи, разпръснати по много места",
        body: "Тъй като чистачите се местят между обектите през деня, нямате преглед на живо кой къде работи — докато някой не се обади.",
      },
      {
        title: "Грешки в заплатите от закръглени часове",
        body: "Без точни часове на влизане/излизане заплатите се възстановяват по памет. Часовете се закръгляват нагоре, разходите се надуват и следват спорове.",
      },
    ],
    features: [
      {
        title: "Отметка за влизане и излизане в реално време",
        body: "Работниците се отчитат на влизане и излизане от телефона си на всяко местоположение, с потвърждение на местоположението; времето е точно, с регистриране на почивките.",
        plan: "Time Basic",
      },
      {
        title: "Автоматично приспадане на почивките",
        body: "Задължителните почивки се изваждат автоматично от работния ден — без ръчни изчисления, без спорове.",
        plan: "Time Basic",
      },
      {
        title: "Преглед на живо на множество местоположения",
        body: "Вижте кои чистачи са на кои местоположения в момента. Забелязвайте пропуските, преди клиентите да го направят.",
      },
      {
        title: "Регистриране на отсъствия с дигитални болнични листове",
        body: "Работниците качват болнични удостоверения и документи за отсъствие директно от приложението. Създайте видовете отсъствия, които екипът Ви наистина използва.",
      },
      {
        title: "Експорт за заплати с едно кликване",
        body: "Точните времеви печати се вливат директно в готов за заплати експорт за доставчика Ви. Без преписване, без грешки.",
      },
    ],
    compliance: {
      label: "Присъствие с потвърждение на местоположението",
      title: "Присъствие в реално време на всяко местоположение за почистване",
      body: "Done-it дава на екипите за почистване отметка с потвърждение на местоположението на всяко място, автоматично приспадане на почивките и преглед на живо кой е на кой обект в момента.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Чистачът пристига на местоположението", body: "Едно докосване го отчита. GPS регистрира обекта и поставя времеви печат на записа. Регистрирането на почивките е вградено в приложението." },
      { step: "Виждате всяко местоположение на живо", body: "Кой е на кой обект в момента — без телефонни обаждания, без догадки. Забелязвайте пропуските в покритието, преди клиентите да го направят." },
      { step: "Заплатите са едно кликване", body: "Всички данни за присъствие — точни часове, местоположения и почивки — се експортират чисто за доставчика Ви за заплати." },
    ],
    stats: [
      { value: "1 докосване", label: "за отметка на всяко местоположение — без нужда от терминал" },
      { value: "На живо", label: "преглед кой е на кой обект в момента" },
      { value: "0", label: "двойни записи — часовете се вливат директно в заплатите" },
    ],
    quote: {
      text: "Най-сетне можем да докажем кой къде и кога е почиствал. Край с присъствените листове, край с клиентските спорове — GPS се грижи за това.",
      who: "Управител, фирма за почистване · 9 служители",
      result: "Нула закръгляне на часовете нагоре",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "installation",
    name: "Монтаж",
    headline: "Отчитане на времето за монтажни екипи — проектни часове, навигация и контрол на бюджета в едно приложение",
    intro: "Изпращайте монтажниците си на правилния адрес с всичко необходимо, свързвайте автоматично всеки отработен час с правилната задача и виждайте оставащия бюджет, преди обектът да приключи. Done-it е най-простият начин да поддържате монтажните проекти навреме и в рамките на разходите.",
    pains: [
      {
        title: "Информацията за задачата не достига до терена навреме",
        body: "Екипите пристигат на обект без адреса, лицето за контакт или подробностите за обхвата. Това първо телефонно обаждане е десет минути изгубено фактурируемо време, преди да бъде отработен дори един час.",
      },
      {
        title: "Часовете не са свързани с правилния проект",
        body: "Когато работниците попълват часовете си ръчно в края на деня — ако изобщо ги попълнят — часовете попадат в грешна задача или изчезват напълно. Фактурите не отговарят на реалността.",
      },
      {
        title: "Преразходът на бюджет се забелязва твърде късно",
        body: "Без обратно отброяване на живо на оставащите часове за всеки проект разбирате, че дадена задача е надхвърлила бюджета, когато издавате фактурата — а не докато все още можете да реагирате.",
      },
      {
        title: "Подизпълнителските часове са трудни за проследяване",
        body: "Подизпълнителите, работещи на същия обект, използват различни системи, попълват часове отделно или изобщо не ги попълват. Съгласуването в края на месеца е бавен и склонен към грешки процес.",
      },
    ],
    features: [
      {
        title: "Информацията за проекта се споделя директно в приложението",
        body: "Работниците отварят проекта на телефона си: адрес, планирани часове, лице за контакт и качени файлове (планове, инструкции, PDF-и). Всичко необходимо е там, преди да пристигнат.",
        plan: "Project Basic",
      },
      {
        title: "Навигация до обекта с един бутон",
        body: "Докоснете веднъж и телефонът навигира до адреса на проекта — вградено в приложението, без нужда от отделно приложение за карти.",
      },
      {
        title: "Обратно отброяване на оставащите часове за всеки проект",
        body: "Задайте планираните часове, когато откривате проекта. Работниците виждат колко часа остават; Вие виждате състоянието на живо в таблото си. Преразходът е видим, преди да се случи.",
        plan: "Project Basic",
      },
      {
        title: "Отметка на работника за всеки проект",
        body: "Всеки работник се отчита към правилния проект. Винаги знаете кой е на кой обект в момента.",
        plan: "Project Basic",
      },
      {
        title: "Регистриране на допълнителна работа и материали",
        body: "Когато обхватът на обекта се разраства, работниците регистрират допълнителната работа и материалите в реално време — с текст и снимки — така че нищо да не се фактурира по памет.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Дигитално отчитане на работното време",
      title: "Отметка с потвърждение на местоположението за всеки проект за монтажни екипи",
      body: "Done-it дава на монтажните екипи отметка с потвърждение на местоположението за всеки проект, обратно отброяване на оставащите часове на живо и автоматичен експорт за заплати — покривайки всяко местно изискване за отчитане на работното време. За екипи, работещи на белгийски строителни обекти, той също така се интегрира с CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Работникът отваря проекта на телефона си", body: "Адрес, контакт, обхват и качени файлове са точно там. Навигира с едно докосване — без обаждания до офиса за упътвания." },
      { step: "Часовете се записват автоматично към правилната задача", body: "Отметката свързва всяка отработена минута с правилния проект. Оставащият бюджет се обновява в реално време." },
      { step: "Допълнителната работа се улавя на обекта", body: "Промените в обхвата, допълнителните материали и забавянията се регистрират с текст и снимки в момента, в който се случват — готови за фактурата." },
    ],
    stats: [
      { value: "В реално време", label: "преглед на оставащите часове за всеки проект" },
      { value: "0", label: "обаждания до офиса за адреси на обекти" },
      { value: "1 кликване", label: "навигация до всеки адрес на проект от приложението" },
    ],
    quote: {
      text: "Часовете попадат автоматично в правилния проект. Изчисляването на себестойността на задачите изведнъж стана лесно — можем да видим реалния марж, преди да приключим дадена задача.",
      who: "Собственик, монтажна фирма · 6 служители",
      result: "Реална себестойност за всяка задача, на живо",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "field-service",
    name: "Изнесено обслужване",
    headline: "Отчитане на времето за изнесени обслужващи екипи — километри, разходи и часове от пътя, всичко в едно приложение",
    intro: "Техници, посещаващи по няколко клиента на ден, касови бележки в жабките, километри, записани по памет. Done-it улавя часовете, километрите и разходите на терен — прикрепени към правилната задача в момента, в който се случват.",
    pains: [
      {
        title: "Километрите се записват дни по-късно — ако изобщо се записват",
        body: "Изискването техниците да възстановяват маршрутите си в края на седмицата означава неточни възстановявания, спорове с клиенти и растяща купчина непредявени километри.",
      },
      {
        title: "Разходи върху хартиени касови бележки, които изчезват",
        body: "Касова бележка в жабка е разход, който може никога да не попадне във фактура. Реалната себестойност за всяка задача винаги е занижена.",
      },
      {
        title: "Няма преглед в реално време къде е екипът",
        body: "Без отметки на живо разбирате къде е даден техник едва когато нещо се обърка и някой се обади.",
      },
      {
        title: "Трудно е да се види действителният марж за всяка задача",
        body: "Когато трудът, пътуването и разходите се проследяват в отделни системи — или изобщо не се проследяват — никога няма единна стойност на себестойността за всяка задача.",
      },
    ],
    features: [
      {
        title: "Автоматично улавяне на километрите (Trax-it)",
        body: "GPS регистрира тръгването, пристигането и точното разстояние за всяко пътуване. Конфигурирайте видове транспорт (собствен автомобил, служебен автомобил, споделено пътуване) със собствени ставки за възстановяване. Без ръчен дневник.",
        plan: "Add-on",
      },
      {
        title: "Управление на разходите на обекта",
        body: "Работниците щракват касова бележка на терен; тя се прикрепя към задачата и е готова за възстановяване — а не да се гони в края на месеца.",
        plan: "Time Basic",
      },
      {
        title: "Контрол на разходите, свързан с проекта",
        body: "Часовете, километрите и разходите се сумират към задачата в реално време. Себестойността за всяка задача е видима, докато задачата все още е отворена.",
        plan: "Project Basic",
      },
      {
        title: "Отметка с потвърждение на местоположението и преглед на екипа на живо",
        body: "Всеки техник се отчита на обекта на клиента. Виждате кой къде е в момента — без обаждания, без догадки.",
      },
      {
        title: "Експорт за заплати с едно кликване",
        body: "Часовете и възстановяванията за километри се експортират чисто за доставчика Ви за заплати — с автоматично приложени конфигурирани от Вас ставки за километри.",
      },
    ],
    compliance: {
      label: "Дигитално проследяване на време и километри",
      title: "GPS часове и автоматични километри за изнесени обслужващи екипи",
      body: "Done-it улавя километрите автоматично за всяко пътуване, прилага конфигурираните от Вас ставки за възстановяване и регистрира всеки километър към правилния клиент или проект. Решението Tyco на ЕС означава, че пътуването от дома до първия клиент се счита за работно време — Done-it проследява и това.",
      slug: "how-to-track-mileage-for-field-teams",
    },
    workflow: [
      { step: "Техникът тръгва за първото повикване", body: "Отметката стартира дневника на пътуването. Километрите се улавят автоматично от тръгването до пристигането — без ръчно въвеждане." },
      { step: "На мястото на работа", body: "Часовете се свързват с правилния проект. Допълнителната работа, материалите и касовите бележки се регистрират в приложението в момента, в който се случват." },
      { step: "Краят на месеца без хаоса", body: "Всеки час, километър и разход вече е прикрепен към правилната задача. Експортирайте към заплати и фактурирайте с реални данни за себестойност." },
    ],
    stats: [
      { value: "Автоматично", label: "улавяне на километрите — без ръчен дневник" },
      { value: "На живо", label: "себестойност за всяка задача: часове + пътуване + разходи" },
      { value: "0", label: "изгубени километри — всяко пътуване се улавя автоматично" },
    ],
    quote: {
      text: "Километрите бяха месечно главоболие. Сега се записват сами — всеки километър е прикрепен към правилния клиент, преди дори да се върнем в офиса.",
      who: "Оперативен директор, фирма за изнесено обслужване · 11 служители",
      result: "Точна себестойност за всяка задача, всеки път",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "hvac",
    name: "ОВК",
    headline: "Отчитане на времето за ОВК техници — себестойност на задачите, части и пътуване в едно приложение",
    intro: "Всяко сервизно повикване има три разхода: труд, части и пътуване. Done-it улавя и трите на терен и ги сумира към правилната задача — така че да знаете маржа си, преди да приключите фактурата.",
    pains: [
      {
        title: "Сервизните повиквания са трудни за точно остойностяване",
        body: "Трудът, частите и пътуването се намират в различни системи — или различни джобове. Реалната себестойност на едно повикване става ясна едва дълго след като техникът е напуснал обекта.",
      },
      {
        title: "Няма доказателство за сервизното време",
        body: "Когато клиент оспорва колко е продължил един ремонт, хартиеният график лесно се оспорва. Потвърдените с GPS времеви печати за влизане и излизане — не.",
      },
      {
        title: "Договорите за поддръжка незабележимо ерозират маржа",
        body: "При повтарящи се договори за поддръжка малките преразходи се натрупват през много посещения. Без брояч на часовете на живо разбирате, че договорът е на загуба едва при подновяване.",
      },
      {
        title: "Техниците забравят да регистрират почивките",
        body: "Ръчните записи на времето пропускат почивките, което води до надплащане и заплати, които не отговарят на реалността.",
      },
    ],
    features: [
      {
        title: "Отчитане на времето на проектна база",
        body: "Всяко сервизно повикване или договор за поддръжка е проект. Часовете, пътуването и материалите се сумират в реално време — давайки Ви видимост на маржа на живо.",
        plan: "Project Basic",
      },
      {
        title: "Допълнителна работа и материали, регистрирани на обекта",
        body: "Когато една задача се разраства, техниците регистрират допълнителната работа и частите с текст и снимки, преди да си тръгнат. Нищо не се фактурира по памет.",
        plan: "Project Basic",
      },
      {
        title: "Километри и транспортни разходи",
        body: "Километрите за всяко повикване се регистрират автоматично и се остойностяват към задачата. Конфигурирайте ставки за възстановяване за всеки вид превозно средство.",
        plan: "Add-on",
      },
      {
        title: "Автоматично приспадане на почивките",
        body: "Задължителните почивки се изваждат автоматично от общото време — без спорове, без грешки в заплатите.",
      },
      {
        title: "Отметка с потвърждение на местоположението и доказателство за присъствие",
        body: "Отметка с времеви печат и потвърждение чрез GPS на всеки обект. Неоспоримо доказателство кога техникът е пристигнал и си е тръгнал.",
      },
    ],
    compliance: {
      label: "Дигитално проследяване на време и разходи",
      title: "Пълно остойностяване на задачите за ОВК сервизни повиквания",
      body: "Done-it дава на ОВК екипите отметка с потвърждение на местоположението за всяко сервизно повикване, видимост на разходите на живо за всяка задача (труд + пътуване + части) и чист експорт за заплати — отговаряйки на всяко местно изискване за отчитане на работното време без допълнителен хардуер.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Техникът се отчита на обекта", body: "GPS поставя времеви печат на пристигането и свързва часовете със сервизното повикване или договора за поддръжка. Без хартия, без терминал." },
      { step: "Допълнителната работа се улавя на момента", body: "Използваните части, допълнителните часове и промените в обхвата се регистрират в приложението със снимки — преди напускане на обекта." },
      { step: "Себестойността на задачата е видима веднага", body: "Трудът, пътуването и материалите се сумират към проекта в реално време. Виждате маржа, преди да издадете фактурата." },
    ],
    stats: [
      { value: "3", label: "вида разходи, уловени за всяко повикване: труд, пътуване, части" },
      { value: "На живо", label: "видимост на маржа за всеки сервизен договор" },
      { value: "0", label: "изненади при фактуриране — разходите са видими в реално време" },
    ],
    quote: {
      text: "Най-сетне знаем какво всъщност ни струва всеки сервизен договор. Изненадите с маржа спряха в деня, в който започнахме да използваме Done-it.",
      who: "Собственик, ОВК фирма · 7 техници",
      result: "Реална себестойност за всяко сервизно повикване, на живо",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "time-tracking-for-field-teams-complete-guide",
    ],
  },

  {
    slug: "electricians",
    name: "Електротехници",
    headline: "Отчитане на времето за електротехници — проектни часове, контрол на бюджета и навигация в едно приложение",
    intro: "Проектни часове, обратно отброяване на оставащия бюджет на живо и навигация до обекта с един бутон — създадено за малки електротехнически екипи, които се нуждаят от контрол над проектите без административната тежест.",
    pains: [
      {
        title: "Часовете не са свързани с правилния проект",
        body: "Когато електротехниците работят по няколко задачи в рамките на седмицата, ръчното разпределяне на часовете в края на деня води до погрешно отнесени разходи, спорове за фактуриране и заплати, които не можете да обясните.",
      },
      {
        title: "Преразходът на бюджет се забелязва твърде късно",
        body: "Без преглед на живо на изразходваните часове спрямо бюджетираните разбирате, че даден проект е надхвърлил бюджета, когато издавате фактурата — а не когато все още можете да реагирате.",
      },
      {
        title: "Изгубено време в намиране на обекта",
        body: "Адреси, въведени в отделно приложение, обаждания до офиса за упътвания, закъснения на обектите на клиентите. Всяка изгубена минута е изгубена фактурируема минута.",
      },
      {
        title: "Подизпълнителските часове са трудни за съгласуване",
        body: "При по-големи монтажи подизпълнителските електротехници проследяват часовете си отделно. Получаването на комбиниран преглед на разходите за труд в края на проекта е ръчна и склонна към грешки задача.",
      },
    ],
    features: [
      {
        title: "Проектни часове с оставащ бюджет на живо",
        body: "Всяка отработена минута се свързва автоматично с правилния проект. Обратно отброяване на живо показва колко часа остават спрямо бюджета — за Вас и за работника на обекта.",
        plan: "Project Basic",
      },
      {
        title: "Навигация до обекта с един бутон",
        body: "Адресът на проекта е в приложението. Едно докосване отваря навигацията — без търсене, без отделно приложение за карти, без обаждания до офиса.",
      },
      {
        title: "Отметка на работника за всеки проект",
        body: "Всеки електротехник се отчита към правилната задача. Винаги знаете кой е на кой обект в момента.",
        plan: "Project Basic",
      },
      {
        title: "Допълнителна работа, регистрирана на обекта",
        body: "Когато обхватът се промени — неочаквана верига, допълнителни материали — това се регистрира в приложението със снимки, преди екипът да си тръгне.",
        plan: "Project Basic",
      },
      {
        title: "Проследяване на извънредния труд с гъвкаво компенсиране",
        body: "Извънредният труд се регистрира в момента, в който се случва. Работниците избират да им бъде изплатен или преобразуван в почивни дни.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Отметка с потвърждение на местоположението за всеки проект",
      title: "Контрол на бюджета на живо и отметка с потвърждение на местоположението за електротехнически екипи",
      body: "Done-it дава на електротехниците отметка с потвърждение на местоположението за всеки проект, бюджет на оставащите часове на живо и експорт за заплати — удовлетворявайки всяко местно изискване за отчитане на работното време. За екипи на белгийски строителни обекти той също така се интегрира с CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Електротехникът навигира до задачата", body: "Адресът и обхватът вече са в приложението. Едно докосване отваря навигацията — без обаждания, без объркване." },
      { step: "Часовете се свързват автоматично с правилния проект", body: "Отметката на обекта свързва всяка отработена минута с правилния проект. Броячът на оставащите часове се обновява на живо." },
      { step: "Промените в обхвата остават в архива", body: "Допълнителната работа и материалите се регистрират на обекта, преди напускане. Фактурата отразява това, което действително се е случило." },
    ],
    stats: [
      { value: "На живо", label: "преглед на оставащите часове за всеки проект" },
      { value: "0", label: "обаждания до офиса за адреси на обекти" },
      { value: "1 докосване", label: "за отметка на всеки обект — без нужда от терминал" },
    ],
    quote: {
      text: "Знаем в реално време дали даден проект надхвърля бюджета. Само това ни спести две лоши изненади тази година.",
      who: "Собственик, електротехническа фирма · 5 електротехници",
      result: "Преразходът на бюджет се улавя, преди да се случи",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "plumbing",
    name: "ВиК",
    headline: "Отчитане на времето за ВиК екипи — бърза отметка, точни заплати и доказателство за присъствие за всяко повикване",
    intro: "Аварийни повиквания, по няколко задачи на ден, часове, попълвани по памет в края на седмицата — или изобщо не попълвани. Done-it превръща това в задача с едно докосване на всеки обект и заплати с едно кликване в края на месеца.",
    pains: [
      {
        title: "Часовете за повикванията, възстановени по памет",
        body: "С няколко задачи на ден ВиК специалистите рядко имат време да попълват точни графици. Часовете се закръгляват или забравят, а заплатите в най-добрия случай са приблизителна оценка.",
      },
      {
        title: "Няма доказателство за времето на обекта",
        body: "Когато клиент оспорва колко е продължил един ремонт, няма обективен запис. Потвърдената с GPS дигитална отметка е лесна за представяне — хартиеният график не е.",
      },
      {
        title: "Километрите между задачите не се проследяват",
        body: "Пътуването между повикванията се натрупва през седмицата и месеца, но без автоматично улавяне рядко се фактурира или възстановява правилно.",
      },
      {
        title: "Подготовката на заплатите е бавна",
        body: "В края на месеца гоненето на графиците, коригирането на грешките и изграждането на експорта за заплати отнема време, което администрацията Ви не би трябвало да отделя.",
      },
    ],
    features: [
      {
        title: "Отметка с потвърждение на местоположението с едно докосване при всяко повикване",
        body: "Пристигането и тръгването се регистрират с едно докосване, с потвърждение на местоположението. Достатъчно бързо за аварийни повиквания; достатъчно точно за спорове.",
        plan: "Time Basic",
      },
      {
        title: "Автоматично приспадане на почивките",
        body: "Задължителните почивки се изваждат автоматично от общото време — нищо за попълване, без грешки в заплатите.",
        plan: "Time Basic",
      },
      {
        title: "Километри, уловени за всяко пътуване",
        body: "Конфигурирайте видове транспорт и ставки за възстановяване. Километрите се записват автоматично и се свързват с правилния клиент или проект.",
        plan: "Add-on",
      },
      {
        title: "Управление на отсъствия и болнични листове",
        body: "Работниците подават болнични листове дигитално от приложението. Създайте видовете отсъствия, които екипът Ви наистина използва, включително прекъсвания заради време.",
      },
      {
        title: "Експорт за заплати с едно кликване",
        body: "Всички данни за часове и километри се експортират чисто за доставчика Ви за заплати — без ръчна подготовка, без Excel.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Дигитално отчитане на работното време",
      title: "Бърза отметка с потвърждение на местоположението за всяко повикване",
      body: "Done-it покрива всяко местно изискване за отчитане на работното време за ВиК екипи — отметка с потвърждение на местоположението с едно докосване за всяко повикване, автоматично приспадане на почивките и чист експорт за заплати. Без хартиени графици, без ръчно възстановяване.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "ВиК специалистът пристига на повикването", body: "Едно докосване го отчита. GPS регистрира местоположението и времето. Достатъчно бързо за аварийна задача." },
      { step: "Тръгването се отбелязва на излизане", body: "Отметката за излизане затваря записа на времето. Километрите до следващата задача започват да се записват автоматично." },
      { step: "Краят на месеца е едно кликване", body: "Всички повиквания, часове и километри се експортират чисто. Подготовката на заплатите се свежда от ден до минути." },
    ],
    stats: [
      { value: "1 докосване", label: "за отметка — достатъчно бързо за аварийни повиквания" },
      { value: "1 кликване", label: "експорт за заплати към доставчика Ви" },
      { value: "0", label: "часове, възстановени по памет в края на месеца" },
    ],
    quote: {
      text: "Преди прекарвахме по една сутрин в края на месеца във възстановяване на часовете. Сега просто експортираме и изпращаме. Доставчикът за заплати ги получава чисти.",
      who: "Собственик, ВиК фирма · 4 ВиК специалисти",
      result: "Подготовката на заплатите намалена от часове до минути",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "landscaping",
    name: "Озеленяване",
    headline: "Отчитане на времето за фирми за озеленяване — сезонни екипи, множество обекти и километри в един преглед",
    intro: "Пиковият сезон означава много обекти, сменящи се екипи и много километри. Done-it поддържа всеки час, всяко отсъствие и всяко пътуване организирани — за да можете да се съсредоточите върху работата, а не върху документацията.",
    pains: [
      {
        title: "Сезонните екипи са сложни за управление",
        body: "Временни работници, променящи се графици и различни размери на екипите правят точното проследяване на часовете почти невъзможно с хартия. Докато съгласувате в края на месеца, винаги нещо липсва.",
      },
      {
        title: "Часовете, разпръснати по много обекти",
        body: "Екип, който посещава три или четири обекта на ден, генерира часове, които трябва да бъдат правилно разпределени. Ръчното въвеждане води до погрешно отнасяне и оспорвани заплати.",
      },
      {
        title: "Прекъсванията заради времето са трудни за документиране",
        body: "Принудителните прекъсвания заради лошо време са реално събитие в озеленяването. Без дигитален запис документирането на прекъсванията и коригирането на заплащането е бавно и склонно към грешки.",
      },
      {
        title: "Километрите между обектите не се проследяват",
        body: "Служебни ванове, лични автомобили и споделени пътувания кръстосват между обектите цял ден. Без автоматично улавяне тези километри никога не попадат в картината на разходите.",
      },
    ],
    features: [
      {
        title: "Преглед на екипа на множество обекти",
        body: "Вижте целия екип, всички обекти в реално време. Знайте кой къде работи в момента — а не в края на месеца.",
      },
      {
        title: "Планиране на смени за сезонни екипи",
        body: "Планирайте кой къде работи, справяйте се с промени в последния момент и изпращайте графика на телефоните на екипа — без обаждане до всеки човек.",
        plan: "Time Pro",
      },
      {
        title: "Персонализирани видове отсъствия",
        body: "Създайте персонализирани видове отсъствия за Вашия сектор: сезонни прекъсвания заради времето, родителски отпуск, болнични дни. Работниците качват документация дигитално от приложението.",
      },
      {
        title: "Километри с проследяване на споделени пътувания",
        body: "Конфигурирайте видове транспорт (служебен ван, личен автомобил, шофьор спрямо пътник при споделено пътуване) със собствени ставки. Километрите се записват автоматично за всяко пътуване.",
        plan: "Add-on",
      },
      {
        title: "Проследяване на извънредния труд с преобразуване в отпуск",
        body: "Извънредният труд в пиковия сезон се натрупва бързо. Всеки работник може да избере да му бъде изплатен или да натрупа часовете като почивни дни — проследявано прозрачно.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Дигитално отчитане на работното време",
      title: "Отметка с потвърждение на местоположението и проследяване на отсъствия за сезонни екипи за озеленяване",
      body: "Done-it дава на екипите за озеленяване дигитално отчитане на работното време на всички обекти и за сезонния персонал — отметка с потвърждение на местоположението, персонализирани видове отсъствия за прекъсвания заради времето и чист експорт за заплати, удовлетворяващ всяко местно изискване.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Екипът се отчита на първия обект", body: "GPS регистрира пристигането. Часовете се разпределят автоматично към правилното местоположение и проект — без ръчно разпределяне в края на деня." },
      { step: "Времето спира работата", body: "Бригадирът регистрира прекъсването от приложението — чист дигитален запис, без допълнителна документация." },
      { step: "Краят на месеца: всички обекти, целият екип, един експорт", body: "Часовете, отсъствията и километрите на всеки обект и за всеки член на екипа се експортират чисто за заплати." },
    ],
    stats: [
      { value: "Автоматично", label: "улавяне на километрите — край с дневника" },
      { value: "На живо", label: "преглед на екипа на всички обекти" },
      { value: "1 кликване", label: "експорт за заплати за всички обекти и целия екип" },
    ],
    quote: {
      text: "Пиковият сезон преди означаваше хаос със заплатите. Сега бригадирът ни проверява приложението, всички са отчетени, а аз експортирам часовете в последния ден на месеца.",
      who: "Собственик, фирма за озеленяване · 12 сезонни работници",
      result: "Край с възстановяването на заплатите в края на месеца",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "maintenance",
    name: "Поддръжка",
    headline: "Отчитане на времето за екипи по поддръжка — повтарящи се посещения на обекти, себестойност на договор и доказателство за присъствие в едно приложение",
    intro: "Повтарящите се договори за поддръжка стоят или падат върху видимостта на разходите. Done-it регистрира всяко посещение, проследява часовете спрямо бюджета на договора и дава на Вас — и на Вашите клиенти — доказателство за присъствие за всяко повикване.",
    pains: [
      {
        title: "Няма видимост на разходите за всеки договор за поддръжка",
        body: "Когато часовете за повтарящи се посещения не се проследяват спрямо конкретния договор, не знаете дали даден клиент е печеливш до годишния преглед — а тогава вече е твърде късно за преоценка.",
      },
      {
        title: "Клиентите очакват доказателство за извършеното",
        body: "Фасилити мениджърите и собствениците на имоти все по-често искат записи за присъствие: кой е дошъл, кога е пристигнал, какво е направил. Хартиен дневник, представен седмици по-късно, не удовлетворява изискванията на SLA.",
      },
      {
        title: "Повтарящите се посещения са трудни за планиране и регистриране",
        body: "Сменящи се смени на много повтарящи се обекти — с персонал, който се замества — създават пъзел от планиране и проследяване на часовете, който ръчните процеси не могат надеждно да решат.",
      },
      {
        title: "Прекъсванията заради времето засягат и поддръжката на открито",
        body: "Поддръжката на зелени площи, фасадите и съоръженията на открито всички са изправени пред прекъсвания заради времето. Правилното им документиране изисква дигитална следа.",
      },
    ],
    features: [
      {
        title: "Проследяване на разходите на база договор",
        body: "Всеки договор за поддръжка е проект. Часовете се записват автоматично спрямо него, давайки Ви стойност на разходите на живо и обратно отброяване на оставащите часове за всеки клиент.",
        plan: "Project Basic",
      },
      {
        title: "Доказателство за присъствие, потвърдено с GPS",
        body: "Отметките с времеви печат и потвърждение на местоположението са неоспорим запис кой е бил на обекта, кога и за колко време — готов за споделяне с клиентите при поискване.",
      },
      {
        title: "Актуализации със снимки от обекта",
        body: "Работниците докладват проблеми, изпълнени задачи или допълнителна работа директно от приложението — с текст и снимки. Клиентите получават запис в реално време; Вие разполагате с документацията за фактуриране.",
        plan: "Project Basic",
      },
      {
        title: "Планиране на повтарящи се смени",
        body: "Планирайте повтарящите се посещения по всички договори, разпределяйте работници и се справяйте със замествания — без обаждане до всеки човек.",
        plan: "Time Pro",
      },
      {
        title: "Видове отсъствия за поддръжка на открито",
        body: "Конфигурирайте прекъсвания заради времето и други видове отсъствия. Работниците ги регистрират от приложението; документацията се качва дигитално.",
      },
    ],
    compliance: {
      label: "Проследяване на времето на база договор",
      title: "Видимост на разходите на живо за всеки договор за поддръжка",
      body: "Done-it дава на екипите по поддръжка доказателство за присъствие, потвърдено с GPS за всеки договор, персонализирани видове отсъствия и преглед на разходите на живо за всеки повтарящ се клиент — удовлетворявайки всяко местно изискване за отчитане на работното време без допълнителен хардуер.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Работникът пристига на обекта за поддръжка", body: "Отметката с потвърждение на местоположението регистрира часа на пристигане и свързва часовете с правилния договор. Доказателството за присъствие се създава автоматично." },
      { step: "Задачите изпълнени, проблемите регистрирани", body: "Извършената работа и всички проблеми се докладват от приложението със снимки. Клиентите могат да видят точно какво е било направено." },
      { step: "Себестойността за всеки договор е видима веднага", body: "Часовете се натрупват спрямо бюджета на договора в реално време. Знаете маржа, преди договорът да се поднови." },
    ],
    stats: [
      { value: "На живо", label: "себестойност за всеки договор за поддръжка" },
      { value: "GPS", label: "доказателство за присъствие, готово за клиентите при поискване" },
      { value: "0", label: "подновявания на договори без да знаете маржа си" },
    ],
    quote: {
      text: "Можем да кажем на фасилити мениджъра точно кой е бил на обекта и кога, за две минути. Това ниво на доказателство промени начина, по който клиентите ни възприемат.",
      who: "Оперативен директор, фирма за поддръжка · 8 работници",
      result: "Клиентските спорове сведени до нула",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
  {
    slug: "retail",
    name: "Магазини и търговия",
    headline: "Отчитане на работното време за магазини — чекиране на смяна, замествания и експорт, готов за заплати, в едно приложение",
    intro: "Отваряща смяна, късна смяна и вечно някой замества някого. Done-it дава на всеки магазин чист цифров запис кой каква смяна е работил — чекиране от собствения телефон, готово за заплатите в края на месеца.",
    pains: [
      {
        title: "Заместванията не стигат до ведомостта",
        body: "Някой покрива болен колега, друг остава час повече при затваряне. Записва се на листче или изобщо не се записва — и заплатите са грешни още преди да сте започнали.",
      },
      {
        title: "Няколко магазина, една ведомост",
        body: "Хората се местят между обектите, работещите на непълно време покриват по два адреса. Събирането на часовете по магазин и ръчното им сливане отнема по един ден всеки месец.",
      },
      {
        title: "Почивките и извънредните часове са на око",
        body: "Задължителните почивки се приспадат по памет, а допълнителните часове се уреждат неформално — точно двете неща, за които проверката пита първо.",
      },
    ],
    features: [
      {
        title: "Чекиране направо в магазина",
        body: "Едно докосване на собствения телефон в началото на смяната. Без терминал на касата, без обща карта, без списък на вратата на склада.",
      },
      {
        title: "Часове по магазин, хора между обектите",
        body: "Всеки магазин е отделен проект. Който покрива друг обект, се чекира към този обект — виждате часовете там, където наистина са отработени.",
      },
      {
        title: "Автоматично приспадане на почивките",
        body: "Задавате правилото за почивка веднъж и то важи за всяка смяна. Извънредният труд се записва в момента, в който възникне, а всеки служител избира изплащане или отгул.",
      },
    ],
    compliance: {
      label: "Отчитане на времето по смени",
      title: "Защитим запис за всяка отработена смяна",
      body: "Done-it пази обективен цифров запис на всяко започване и приключване на работа, с почивките и извънредните часове — записът, който одит или спор за заплата изисква.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Хората се чекират за смяната", body: "Едно докосване на телефона в началото на смяната. Заместването в друг обект се отчита към него." },
      { step: "Виждате кой е на работа, по магазини", body: "Изглед в реално време за всички обекти — забелязвате незапълнена отваряща смяна, преди вратите да се отворят." },
      { step: "Заплатите с един експорт", body: "Часове, почивки и извънреден труд по служител и по магазин, експортирани готови за счетоводството." },
    ],
    stats: [
      { value: "1 докосване", label: "за чекиране — без терминал на касата" },
      { value: "На живо", label: "кой е на работа, във всички магазини" },
      { value: "0", label: "загубени замествания между щанда и ведомостта" },
    ],
    quote: {
      text: "Два магазина и много хора на непълно време. Преди половината заместващи смени се уреждаха по памет. Сега всеки час е на правилния магазин и заплатите отнемат минути.",
      who: "Собственик, търговия · 11 служители",
      result: "Подготовката за заплати — от ден на под час",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Офиси и професионални услуги",
    headline: "Отчитане на времето за офиси — часове по клиент и проект, без електронна таблица",
    intro: "Счетоводители, нотариуси, архитекти, агенции и консултанти таксуват времето си — или поне трябва да знаят къде е отишло. Done-it записва часовете към правилния клиент и проект, от бюрото или на път.",
    pains: [
      {
        title: "Часовете за фактуриране, възстановени в петък",
        body: "Време, записано в края на седмицата, е време по предположение. Цели часове тихо изчезват между работата и фактурата.",
      },
      {
        title: "Никой не знае колко наистина струва един клиент",
        body: "Хонорарът е договорен преди месеци. Дали работата още се събира в него, става ясно едва когато някой седне да сметне — обикновено твърде късно.",
      },
      {
        title: "Хибридната работа прави записа на парче",
        body: "Част от екипа е в офиса, част у дома, част при клиента. Присъствието спира да е факт и се превръща в реконструкция.",
      },
    ],
    features: [
      {
        title: "Часове към клиент и проект",
        body: "Всеки запис попада в проект, така че виждате часовете по клиент, без да експортирате нищо и без да питате никого.",
      },
      {
        title: "Работи на бюрото и извън офиса",
        body: "Едно и също приложение на телефона или в браузъра. Посещението при клиент се отчита като работа по този клиент, включително пътуването, ако искате.",
      },
      {
        title: "Извънреден труд и отпуски на едно място",
        body: "Допълнителните часове се записват в момента, отпуските се заявяват и одобряват в приложението — без паралелна таблица кой кога отсъства.",
      },
    ],
    compliance: {
      label: "Отчитане на времето по клиенти",
      title: "Обективен запис на отработените часове",
      body: "Done-it пази цифров запис на работното време по служител, защитен от подмяна — основата както за заплатите, така и за защитима фактура към клиента.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Отваряте проект за клиента", body: "Настройвате го веднъж — клиента и, ако таксувате по бюджет, планираните часове." },
      { step: "Екипът записва времето си към него", body: "От бюрото или от телефона. Всеки час попада при клиент, а не в нечия памет." },
      { step: "Виждате часовете по клиент, експортирате за заплати", body: "Текущи суми по проект и чист експорт за заплати или фактуриране в края на месеца." },
    ],
    stats: [
      { value: "По клиент", label: "часове, видими още докато работата тече" },
      { value: "Офис или път", label: "един и същ запис, телефон и браузър" },
      { value: "1 експорт", label: "за заплати и фактуриране" },
    ],
    quote: {
      text: "Преди възстановявахме цялата седмица всеки петък. Сега часовете вече са при правилния клиент и виждам кои преписки изяждат хонорара.",
      who: "Съдружник, счетоводна кантора · 8 служители",
      result: "Възстановени часове за фактуриране всеки месец",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Салони и грижа за тялото",
    headline: "Отчитане на времето за салони — смени, часове на непълно време и заплати без хартиен тефтер",
    intro: "Фризьорските, бръснарските, маникюрните и козметичните студиа работят на смени и на непълно работно време. Done-it записва кой кога е работил, приспада почивките автоматично и подава на счетоводството чист файл.",
    pains: [
      {
        title: "Договори на непълно време, безброй варианти",
        body: "Три дни едната седмица, четири следващата, събота, разменена за вторник. Договорените и отработените часове се разминават бързо.",
      },
      {
        title: "Тефтерът с часовете не е отчет на работното време",
        body: "Записаните часове показват какво е било запазено, не кой е работил. Когато счетоводството или проверка попита, системата за резервации е грешният отговор.",
      },
      {
        title: "Допълнителните часове се уреждат неформално",
        body: "Оставането за последен клиент се помни, обещава се и понякога се забравя. Точно от такива пропуски тръгват споровете.",
      },
    ],
    features: [
      {
        title: "Чекиране в салона",
        body: "Едно докосване на собствения телефон в началото на смяната — без общ терминал на рецепцията, без списък за подпис до огледалото.",
      },
      {
        title: "Почивки и правила за непълно време — веднъж",
        body: "Задавате правилото за почивка и стандартните часове веднъж; важат за всяка смяна, с изключения по човек, където договорът е различен.",
      },
      {
        title: "Извънреден труд — платен или взет като отгул",
        body: "Допълнителните часове се записват докато се работят и всеки служител избира изплащане или отгул — уредено в приложението, не по памет.",
      },
    ],
    compliance: {
      label: "Цифрово отчитане на времето",
      title: "Запис, който важи и извън тефтера с часовете",
      body: "Done-it записва реалното работно време по служител, с почивките и извънредния труд — отделно от резервациите и защитимо при проверка.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Хората се чекират за смяната", body: "Едно докосване при идване. Почивките се приспадат по правилото, което сте задали веднъж." },
      { step: "Виждате деня в движение", body: "Кой е на работа, кой е в почивка, кой е останал по-късно — на живо, без да питате." },
      { step: "Счетоводството получава чист файл", body: "Отработени часове, почивки и извънреден труд по служител, експортирани за обработката на заплатите." },
    ],
    stats: [
      { value: "1 докосване", label: "за чекиране — нищо общо, нищо на хартия" },
      { value: "Авто", label: "приспадане на почивката при всяка смяна" },
      { value: "0", label: "късни часове, уредени по памет" },
    ],
    quote: {
      text: "Всички са на непълно време и всяка седмица е различна. Приложението просто го записва — спрях да сглобявам месеца в таблица.",
      who: "Собственик, фризьорски салон · 6 служители",
      result: "Край на спорните часове в края на месеца",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Здраве и уелнес",
    headline: "Отчитане на времето за фитнеси, студиа и спа центрове — смени, тренировки и заплати в едно приложение",
    intro: "Фитнес залите, студиата, физиотерапевтичните практики и спа центровете работят с дълго работно време и сменящ се персонал. Done-it записва кой коя смяна е покрил, приспада почивките автоматично и експортира заплатите чисто.",
    pains: [
      {
        title: "Дълго работно време, постоянни замествания",
        body: "Ранни смени, вечерни тренировки и уикенди, покривани от смес от щатни, почасови и външни специалисти. Графикът е план, не отчет.",
      },
      {
        title: "Часовете в залата и на рецепцията се смесват",
        body: "Инструктор води две тренировки и междувременно поема рецепцията. Да го платите правилно значи да знаете какво е отработено, а не какво е било планирано.",
      },
      {
        title: "Няколко обекта, една администрация",
        body: "Хората се местят между студиата. Ръчното сливане на часовете им всеки месец е бавно и води до грешки.",
      },
    ],
    features: [
      {
        title: "Чекиране по студио",
        body: "Едно докосване на телефона в обекта, където работят. Заместването в друго студио попада в часовете на това студио.",
      },
      {
        title: "Изглед на живо кой е на работа",
        body: "Виждате залата във всички обекти в реално време — незапълнена ранна смяна се вижда преди отварянето, а не след това.",
      },
      {
        title: "Почивките и извънредният труд — решени",
        body: "Правилата за почивка се прилагат автоматично, допълнителните часове се записват в момента, с изплащане или отгул по избор на служителя.",
      },
    ],
    compliance: {
      label: "Отчитане на смени в няколко обекта",
      title: "Обективен запис за всяка смяна и всеки обект",
      body: "Done-it пази цифров запис на работното време по служител и по обект — основата за правилно заплащане и за всяка проверка на отработените часове.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Хората се чекират в студиото", body: "Едно докосване в началото на смяната, в обекта, където работят." },
      { step: "Заместванията се виждат на живо", body: "Кой е на работа и в кой обект в момента, за да запълните дупките, преди клиентите да ги усетят." },
      { step: "Един експорт за заплатите", body: "Часове, почивки и извънреден труд по служител и по студио, готови за счетоводството." },
    ],
    stats: [
      { value: "По студио", label: "часове, дори когато хората заместват другаде" },
      { value: "На живо", label: "изглед на залата във всички обекти" },
      { value: "Авто", label: "почивки и извънреден труд, без ръчни сметки" },
    ],
    quote: {
      text: "Две студиа, дълго работно време и много заместващи смени. Сега часовете попадат в правилния обект и заплатите са един файл.",
      who: "Мениджър, фитнес студио · 14 служители",
      result: "Графикът и заплатите най-накрая съвпадат",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Ресторанти и хотели",
    headline: "Отчитане на времето за кафенета, ресторанти и хотели — смени, почивки и извънреден труд, уредени честно",
    intro: "Разкъсани смени, късни затваряния и събота, която се проточва. Done-it записва часовете така, както са отработени, приспада почивките по вашето правило и дава на счетоводството един чист файл.",
    pains: [
      {
        title: "Смяната никога не свършва, когато казва графикът",
        body: "Обслужването се проточва, затварянето отнема още половин час. Тези минути са реална работа, а уреждането им по памет е началото на споровете.",
      },
      {
        title: "Разкъсаните смени трудно се отчитат",
        body: "Обяд, почивка, после вечеря. На хартия става един дълъг блок или две предположения — нито едното не е истината.",
      },
      {
        title: "Голямо текучество, постоянно обучение",
        body: "Нови хора всеки сезон. Система, която изисква обучение, няма да оцелее и една натоварена седмица.",
      },
    ],
    features: [
      {
        title: "Чекиране в началото и края на всяко обслужване",
        body: "Едно докосване в началото и края на всеки блок, така че разкъсаните смени се записват както са протекли — а не слети в една.",
      },
      {
        title: "Почивки по вашето правило",
        body: "Задължителните почивки се приспадат автоматично, с изключения по човек, където договорът го изисква. Без ръчни сметки в края на месеца.",
      },
      {
        title: "Извънредният труд се урежда в приложението",
        body: "Допълнителният половин час след затваряне се записва в момента, в който е отработен, и се изплаща или се взема като отгул — служителят избира.",
      },
    ],
    compliance: {
      label: "Отчитане на разкъсани смени",
      title: "Защитим запис на часовете при голямо текучество",
      body: "Done-it пази обективен цифров запис на всяка смяна, почивка и допълнителен час — точно това, което проверка на заплатите или инспекция по труда иска да види.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Хората се чекират за обслужването", body: "Едно докосване в началото на блока, същото в края. Разкъсаните смени остават разделени." },
      { step: "Виждате залата на живо", body: "Кой е на смяна за довечера, кой е още в почивка, кой е останал след затварянето." },
      { step: "Счетоводството получава реалните часове", body: "Отработено време, почивки и извънреден труд по служител, експортирани в един файл." },
    ],
    stats: [
      { value: "Разкъсани смени", label: "записани като два блока, не като едно предположение" },
      { value: "Авто", label: "приспадане на почивката по вашето правило" },
      { value: "1 мин", label: "за въвеждане на нов човек — това е собственият му телефон" },
    ],
    quote: {
      text: "Затварянето винаги се проточва. Сега тези минути се записват, вместо да се спори за тях, и хората могат да си ги вземат като отгул.",
      who: "Собственик, ресторант · 17 служители",
      result: "По-малко спорове, по-бързи заплати",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Здравеопазване и грижа",
    headline: "Отчитане на времето за екипи в грижата — смени, домашни посещения и квалификации, в един запис",
    intro: "Практиките, екипите за домашни грижи и домовете за възрастни работят на смени, посещения и стриктна документация. Done-it отчита отработеното време по смяна и по посещение и пази документите на служителите там, където ще ги намерите.",
    pains: [
      {
        title: "Посещенията и смените са в различни системи",
        body: "Домашните посещения са в един инструмент, графикът — в друг, а счетоводството получава трета версия. Изравняването им е месечна задача.",
      },
      {
        title: "Нощните и почивните часове се плащат грешно",
        body: "Нощният труд и уикендите имат различни ставки. Когато записът е приблизителен, такова е и заплащането — а корекцията идва по-късно.",
      },
      {
        title: "Сертификатите изтичат незабелязано",
        body: "Квалификациите и сертификатите имат значение в грижата, а стоят в папка, която никой не отваря до първата проверка.",
      },
    ],
    features: [
      {
        title: "Чекиране по смяна или по посещение",
        body: "Едно докосване в началото на смяната или на всеки адрес при домашни грижи — с потвърдено местоположение там, където ви трябва доказателство.",
      },
      {
        title: "Часовете, разделени както ги плащате",
        body: "Нощните, почивните и редовните часове се записват поотделно, така че експортът за заплати вече отразява как работата реално се плаща.",
      },
      {
        title: "Документите на служителите на едно място",
        body: "Договори, сертификати и документи за самоличност се пазят при човека, за когото се отнасят — намират се за секунди, когато някой ги поиска.",
      },
    ],
    compliance: {
      label: "Отчитане на смени и посещения",
      title: "Записи, които издържат проверка",
      body: "Done-it пази цифров, обективен запис на работното време по служител — часове, почивки и разделение на нощния и почивния труд — заедно с документите, които вървят с всеки човек.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Чекиране за смяната или посещението", body: "Едно докосване в обекта или на адреса на клиента, с потвърдено местоположение там, където трябва доказателство." },
      { step: "Часовете попадат в правилната категория", body: "Редовните, нощните и почивните часове се записват поотделно, в момента на възникване." },
      { step: "Експорт за заплатите", body: "Един чист файл за период, вече разделен така, както плащате." },
    ],
    stats: [
      { value: "По посещение", label: "или по смяна — както работи вашият екип" },
      { value: "Разделени", label: "нощни и почивни часове, записани както са отработени" },
      { value: "Подредени", label: "сертификати, съхранени по служител" },
    ],
    quote: {
      text: "Домашните посещения и смените по график най-накрая са в един запис. Заплатите спряха да бъдат упражнение по изравняване.",
      who: "Координатор, екип за домашни грижи · 22 служители",
      result: "Един запис вместо три",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
